import { mutationOptions, type QueryClient } from "@tanstack/react-query";
import { apiDelete, apiPost } from "@softmax/observatory-client/client";
import { requestUnavailable } from "@softmax/observatory-client/transport";
import type {
  CredentialCreateResponse,
  PlayerCredentialCreateResponse,
} from "@softmax/observatory-client/types";
import {
  credentialPath,
  credentialsKey,
  type CredentialRecord,
  type CredentialTarget,
} from "./credentials.queries";
import { isUncertainWrite } from "../write-failure";

type CredentialAction =
  | { kind: "create"; name: string; allowWrite: boolean }
  | { kind: "revoke"; id: string };
export function credentialMutationOptions({
  queryClient,
  viewerId,
  target,
  onCreated,
}: {
  queryClient: QueryClient;
  viewerId: string;
  target: CredentialTarget;
  onCreated(token: string): void;
}) {
  const key = credentialsKey({ viewerId, target });
  return mutationOptions({
    mutationFn: async (action: CredentialAction): Promise<void> => {
      const path = credentialPath({ target });

      if (action.kind === "revoke") {
        await apiDelete<void>(
          `${path}/${encodeURIComponent(action.id)}`,
          undefined,
          { elevatedPrivileges: false },
        );
        await queryClient.cancelQueries({ queryKey: key, exact: true });

        if (!requestUnavailable(queryClient.getQueryState(key)?.error ?? null))
          queryClient.setQueryData<CredentialRecord[]>(key, (rows) =>
            rows?.filter((row) => row.id !== action.id),
          );

        return;
      }

      const result = await apiPost<
        CredentialCreateResponse | PlayerCredentialCreateResponse
      >(
        path,
        target.kind === "user"
          ? { name: action.name, scopes: action.allowWrite ? ["write"] : [] }
          : { name: action.name },
        { elevatedPrivileges: false },
      );
      const { token, ...record } = result;
      await queryClient.cancelQueries({ queryKey: key, exact: true });

      if (!requestUnavailable(queryClient.getQueryState(key)?.error ?? null))
        queryClient.setQueryData<CredentialRecord[]>(key, (rows) =>
          rows === undefined ? undefined : [record, ...rows],
        );

      onCreated(token);
    },
    onMutate: () => queryClient.cancelQueries({ queryKey: key, exact: true }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: key, exact: true });
    },
    onError: (error) => {
      if (requestUnavailable(error) || isUncertainWrite(error))
        void queryClient.invalidateQueries({ queryKey: key, exact: true });
    },
    retry: false,
  });
}
